import axios, { AxiosRequestHeaders } from 'axios'

import store from '@/store'
import { ICart, ICartQuoteResponse, ICreateCartPayload, IQuoteCartPayload, IUpdateCartPayload } from '@/store/modules/cart'
import dayjs from './dayjs'
import { IFacility } from '@/store/modules/facility'
import { IVacancies } from '@/store/modules/timeslot'
import { getActivities, getVacancies } from './facilitiesBooking'
import { SET_CART_ACTIVITIES, SET_CART_VACANCIES } from '@/store/types'
import { transformMetadata } from './cartMetadata'

export { transformMetadata } from './cartMetadata'

function getHeaders(): AxiosRequestHeaders {
    if (store.getters.getToken) {
        return {
            'x-ticket-token': store.getters.getToken,
        }
    } else {
        return {
            'api-key': store.getters.apiKey,
        }
    }
}

export async function getCart(id: string): Promise<ICart> {
    const { data: cart } = await axios.get<ICart>(`/shopify/carts/${id}`, {
        headers: getHeaders(),
    })
    const activitiesMap: Record<string, IFacility[]> = store.getters.cartActivities
    const vacanciesMap: Record<string, Record<string, IVacancies[]>> = store.getters.cartVacancies

    const newCart = {
        ...cart,
        cartItems: await Promise.all(cart.cartItems.map(async cartItem => {
            const {
                sku = '',
                dateOfVisit = '',
                start = '',
                end = '',
            } = transformMetadata(cartItem.item.metadata)
            if (!activitiesMap[sku]) {
                const activities = await getActivities(sku)
                activitiesMap[sku] = typeof activities !== 'string' ? activities : []
            }
            const facilityId = activitiesMap[sku][0]._id
            if (!vacanciesMap[sku]?.[dateOfVisit] && facilityId) {
                const isBeforeToday = dayjs(dateOfVisit).isBefore(dayjs(), 'date')
                const vacancies = await getVacancies(facilityId, dateOfVisit)
                vacanciesMap[sku] = {
                    ...(vacanciesMap[sku] ?? {}),
                    [dateOfVisit]: !isBeforeToday && typeof vacancies !== 'string' ? vacancies : []
                }
            }
            return {
                ...cartItem,
                activity: activitiesMap[sku].length ? activitiesMap[sku][0] : undefined,
                vacancy: vacanciesMap[sku][dateOfVisit].length
                    ? vacanciesMap[sku][dateOfVisit].find(
                        vacancy => vacancy.start === start && vacancy.end === end
                    )
                    : undefined
            }
        }))
    }

    store.commit(SET_CART_ACTIVITIES, activitiesMap)
    store.commit(SET_CART_VACANCIES, vacanciesMap)

    return newCart
}

export async function createCart(payload: ICreateCartPayload): Promise<ICart> {
    const { data } = await axios.post('/shopify/carts', payload, {
        headers: getHeaders(),
    })
    return data
}

export async function updateCart(id: string, payload: IUpdateCartPayload): Promise<ICart> {
    const { data } = await axios.put(`/shopify/carts/${id}`, payload, {
        headers: getHeaders(),
    })
    return data
}

export async function quoteCart(payload: IQuoteCartPayload): Promise<ICartQuoteResponse> {
    const { data } = await axios.post<ICartQuoteResponse>('/shopify/carts/quote', payload, {
        headers: getHeaders(),
    })
    return data
}

export function canApplyCartQuote(quote: ICartQuoteResponse, isDiscountPreviewEnable: boolean): boolean {
    return isDiscountPreviewEnable || !(quote.totalDiscountAmount > 0 ||
        quote.discounts.some(discount => discount.amount > 0) ||
        quote.lines.some(line => line.discountAmount > 0 || line.discountedTotalAmount < line.originalTotalAmount))
}

export async function deleteCart(id: string): Promise<ICart> {
    const { data } = await axios.delete(`/shopify/carts/${id}`, {
        headers: getHeaders(),
    })
    return data
}

export function isCartExpired(cart?: ICart): boolean {
    if (cart) {
        const userId = store.getters.userId
        if (cart.user && userId && cart.user !== userId) {
            return true
        }
        const cartExpiredAt = dayjs(cart.expiredAt)
        return dayjs().isAfter(cartExpiredAt)
    } else {
        return false
    }
}
